import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Checkbox from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import LinkItem from '@atlaskit/menu/link-item';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import { useAppSelector } from '../../../../../common/ui/app-tile/app-selector';
import { UnsafeTileInner } from '../../../../../common/ui/app-tile/unsafe-tile';
import type { AppsGroupItemProps } from '../../../../../common/ui/more-apps/apps-group/apps-group-item/types';

import { messages } from './messages';

const styles = cssMap({
	container: {
		alignItems: 'center',
	},
	linkWrapper: {
		borderRadius: token('radius.medium'),
		// Hack to render the rounded corners because <LinkItem /> isn't styleable with XCSS
		overflow: 'hidden',
		width: '100%',
	},
	emptyCheckboxSpace: {
		width: '24px',
		height: '24px',
	},
});

export const AppsGroupItem = (props: AppsGroupItemProps): React.JSX.Element => {
	const {
		icon,
		isCustomizing,
		isSelectable,
		isSelected = false,
		onToggleSelect,
		title,
		url,
		productId,
		iconUrl,
	} = props;
	const productIcon = useAppSelector(productId);
	const intl = useIntl();

	const renderableIcon = useMemo(() => {
		if (productIcon) {
			return <UnsafeTileInner logo={productIcon.icon} isSelected={true} size="small" />;
		}

		return (
			<Tile size="small" label="" isInset={false}>
				{icon || <img src={iconUrl} alt={title} width={24} height={24} />}
			</Tile>
		);
	}, [icon, iconUrl, productIcon, title]);

	const onToggleSelectCheckbox = useCallback(() => {
		onToggleSelect?.(productId);
	}, [onToggleSelect, productId]);

	return (
		<Inline space="space.100" xcss={styles.container}>
			{isCustomizing &&
				(isSelectable ? (
					<Checkbox
						isChecked={isSelected}
						onChange={onToggleSelectCheckbox}
						aria-label={intl.formatMessage(messages.selectAppCheckboxLabel, { appName: title })}
					/>
				) : (
					<Box xcss={styles.emptyCheckboxSpace} />
				))}
			<Box xcss={styles.linkWrapper}>
				<LinkItem href={url} iconBefore={renderableIcon}>
					{title}
				</LinkItem>
			</Box>
		</Inline>
	);
};
